import {createSearchParams, Route, Routes, useLocation, useNavigate} from "react-router";
import AuthLayout from "@/layouts/AuthLayout.tsx";

import DashboardLayout from "@/layouts/DashboardLayout.tsx";
import DocumentPage from "@/pages/documents/page.tsx";
import {update} from "@/reducers/authReducer.ts";
import {useDispatch} from "react-redux";
import Users from "@/pages/settings/Users.tsx";
import Account from "@/pages/settings/Account.tsx";


import gsap from 'gsap';
import {Flip} from 'gsap/Flip';
import {useGSAP} from '@gsap/react';
import EditorPage from "@/pages/editor/EditorPage.tsx";
import {Toaster} from "@/components/ui/sonner.tsx";
import PermissionProvider from "@/components/permissions/PermissionProvider.tsx";

import EditUser from "@/pages/settings/EditUser.tsx";
import Dashboard from "@/pages/dashboard/dashboard.tsx";
import Overview from "@/pages/documents/overview.tsx";
import {hasAuthParams, useAuth} from "react-oidc-context";
import axios from "axios";
import Loading from "@/components/loading.tsx";
import {keycloakAdminApi, keycloakApi} from "@/main.tsx";
import CompaniesPage from "@/pages/companies/page.tsx";
import {ErrorBoundary} from "@sentry/react";
import ErrorPage from "@/components/error-page.tsx";
import NotFoundPage from "@/pages/not-found-page.tsx";
import React, {useEffect, useState} from "react";
import logo from "@/assets/logo_ikon_szurke.svg";
import {Button} from "@/components/ui/button.tsx";
import FilesPage from "@/pages/files-page.tsx";

gsap.registerPlugin(useGSAP);
gsap.registerPlugin(Flip);

const SignIn = React.lazy(() => import("@/pages/auth/SignIn.tsx"));

function App() {
    const auth = useAuth();
    const [hasTriedSignin, setHasTriedSignin] = React.useState(false);

    // automatically sign-in
    React.useEffect(() => {
        if (!hasAuthParams() &&
            !auth.isAuthenticated && !auth.activeNavigator && !auth.isLoading &&
            !hasTriedSignin
        ) {
            auth.signinRedirect();
            setHasTriedSignin(true);
        }
    }, [auth, hasTriedSignin]);

    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    const [permissions, setPermissions] = useState<Dictionary<boolean>>({})

    useEffect(() => {
        if (auth.isAuthenticated) {
            keycloakApi.get("/account").then((res) => {
                dispatch(update({
                    ...res.data,
                    token: auth?.user?.profile
                }))
            }).catch(err => {
                console.error(err)
            })
        }
    }, [auth])

    async function hasPermission(permission: string) {
        if (permissions[permission] !== undefined) {
            return permissions[permission]
        }
        if (!auth.isAuthenticated) {
            return false
        }
        const params = new URLSearchParams()
        params.append('grant_type', 'urn:ietf:params:oauth:grant-type:uma-ticket')
        params.append('audience', 'vsp-api')
        params.append('permission', permission)
        params.append("response_mode", "decision")
        try {
            const uma2 = await keycloakApi.post(`/protocol/openid-connect/token`, params)
            if (uma2.status !== 200) {
                setPermissions((prev) => ({
                    ...prev,
                    [permission]: false
                }))
                return false
            }
            const result = uma2.data.result
            setPermissions((prev) => ({
                ...prev,
                [permission]: result
            }))
            return result
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (e: any) {
            setPermissions((prev) => ({
                ...prev,
                [permission]: false
            }))
            return false
        }
    }

    const LoadingScreen = () => (
        <div className={"w-screen h-screen"}>
            <Loading/>
        </div>
    )

    switch (auth.activeNavigator) {
        case "signinSilent":
            return <LoadingScreen/>;
        case "signoutRedirect":
            return <LoadingScreen/>;
    }

    if (auth.isLoading) {
        return <LoadingScreen/>;
    }

    if (auth.error) {
        console.error(auth.error.message)
        return (<div className={"w-screen h-screen flex flex-col items-center justify-center"}>

            <img src={logo} alt={"logo"} draggable={"false"} className={"max-w-12 box"}/>
            <div className={"font-bold text-xl"}>Lejárt munkamenet</div>
            <Button onClick={() => auth.signinRedirect()} className={"mt-4 font-bold"}>Bejelentkezés</Button>
        </div>);
    }

    if (auth.isAuthenticated) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${auth.user?.access_token}`
        keycloakApi.defaults.headers.common['Authorization'] = `Bearer ${auth.user?.access_token}`
        keycloakAdminApi.defaults.headers.common['Authorization'] = `Bearer ${auth.user?.access_token}`
    } else {
        if (!location.pathname.includes('signin')) {
            if (location.pathname != "/") {
                navigate({
                    pathname: "/signin",
                    search: createSearchParams({
                        next: location.pathname
                    }).toString()
                })
            } else {
                navigate('signin')
            }
        }
    }


    return (
        <PermissionProvider fetchPermission={hasPermission}>
            <ErrorBoundary fallback={<ErrorPage/>}>
                <Routes>
                    <Route element={<DashboardLayout/>}>
                        <Route index path="/" element={<Dashboard/>}/>
                        <Route path={"documents"} element={<DocumentPage/>}/>
                        <Route path={"documents/:id"} element={<Overview/>}/>
                        <Route path={"documents/:id/edit"} element={<EditorPage/>}/>

                        <Route path={"database/account"} element={<Account/>}/>
                        <Route path={"database/users"} element={<Users/>}/>
                        <Route path={"database/users/:id"} element={<EditUser/>}/>

                        <Route path={"database/companies"} element={<CompaniesPage/>}/>
                        <Route path={"database/files"} element={<FilesPage/>}/>
                    </Route>

                    <Route element={<AuthLayout/>}>
                        <Route path="signin" element={<SignIn/>}/>
                    </Route>
                    <Route path={"*"} element={<NotFoundPage/>}/>

                </Routes>
            </ErrorBoundary>
            <Toaster/>
        </PermissionProvider>
    );
}

export default App